import { normalizeReceiptCharactersPerLine } from "@/lib/receiptPreview";

export const RECEIPT_LOGO_NARROW_MAX_WIDTH_PX = 256;
export const RECEIPT_LOGO_WIDE_MAX_WIDTH_PX = 384;

export function getReceiptLogoMaxWidthPx(
  charactersPerLine?: number | null,
  logoWidthPercent?: number | null,
): number {
  const baseWidth =
    normalizeReceiptCharactersPerLine(charactersPerLine) <= 36
      ? RECEIPT_LOGO_NARROW_MAX_WIDTH_PX
      : RECEIPT_LOGO_WIDE_MAX_WIDTH_PX;
  const percent = normalizeReceiptLogoWidthPercent(logoWidthPercent);
  return Math.max(1, Math.round((baseWidth * percent) / 100));
}

export const RECEIPT_LOGO_WIDTH_PERCENT_OPTIONS = [25, 50, 75, 100] as const;
export type ReceiptLogoWidthPercent = (typeof RECEIPT_LOGO_WIDTH_PERCENT_OPTIONS)[number];
export const DEFAULT_RECEIPT_LOGO_WIDTH_PERCENT = 100;

export function normalizeReceiptLogoWidthPercent(value?: number | null): ReceiptLogoWidthPercent {
  if (value === 25 || value === 50 || value === 75 || value === 100) {
    return value;
  }
  if (typeof value === "number" && value >= 25 && value <= 100) {
    return RECEIPT_LOGO_WIDTH_PERCENT_OPTIONS.reduce((closest, option) =>
      Math.abs(option - value) < Math.abs(closest - value) ? option : closest,
    );
  }
  return DEFAULT_RECEIPT_LOGO_WIDTH_PERCENT;
}

function loadImage(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Logo konnte nicht geladen werden."));
    image.src = dataUrl;
  });
}

/**
 * Simuliert die ESC/POS-Logoaufbereitung: Skalierung auf Bonbreite und 1-Bit-Schwellwert.
 */
export async function renderReceiptLogoPreview(
  logoDataUrl: string,
  charactersPerLine?: number | null,
  logoWidthPercent?: number | null,
): Promise<string> {
  const image = await loadImage(logoDataUrl);
  const maxWidth = getReceiptLogoMaxWidthPx(charactersPerLine, logoWidthPercent);
  const scale = image.width > maxWidth ? maxWidth / image.width : 1;
  const targetWidth = Math.max(1, Math.round(image.width * scale));
  const targetHeight = Math.max(1, Math.round(image.height * scale));

  const canvas = document.createElement("canvas");
  canvas.width = targetWidth;
  canvas.height = targetHeight;
  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("Canvas nicht verfügbar.");
  }

  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, targetWidth, targetHeight);
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = "high";
  context.drawImage(image, 0, 0, targetWidth, targetHeight);

  const imageData = context.getImageData(0, 0, targetWidth, targetHeight);
  for (let index = 0; index < imageData.data.length; index += 4) {
    const alpha = imageData.data[index + 3] ?? 255;
    const red = imageData.data[index] ?? 0;
    const green = imageData.data[index + 1] ?? 0;
    const blue = imageData.data[index + 2] ?? 0;
    const luminance = red * 0.299 + green * 0.587 + blue * 0.114;
    const isBlack = luminance < 160 && alpha > 32;
    const value = isBlack ? 0 : 255;
    imageData.data[index] = value;
    imageData.data[index + 1] = value;
    imageData.data[index + 2] = value;
    imageData.data[index + 3] = 255;
  }

  context.putImageData(imageData, 0, 0);
  return canvas.toDataURL("image/png");
}
